// @ts-nocheck
/*
 * Port of views/dashboard/pending-prompts.js.
 * API: API_PROMPT_ACTIVE_LIST + POST /v2/prompt/:id/language-status.
 * SUPERADMIN only, 45s auto-refresh + countdown.
 * Column count preserved: 4 (Name, Total Duration, Created By, Actions).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { CheckCircle2, Download as DownloadIcon, Eye, Play, RefreshCw, Square, XCircle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_PROMPT_ACTIVE_LIST } from '@/service/url';
import { PROMPT_STATUS, USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';

const AUTO_REFRESH_SECONDS = 45;

export default function PendingPromptsTable() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [filesModal, setFilesModal] = useState({ visible: false, entries: [], promptName: '', promptId: null });
  const [playingUrl, setPlayingUrl] = useState('');
  const [refreshCountdownSec, setRefreshCountdownSec] = useState(AUTO_REFRESH_SECONDS);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {}
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchPrompts(true);
    const refreshInterval = setInterval(() => {
      if (!document.hidden) fetchPrompts(false); // skip in background tabs
      setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
    }, AUTO_REFRESH_SECONDS * 1000);
    const countdownInterval = setInterval(() => {
      setRefreshCountdownSec((prev) => (prev <= 1 ? AUTO_REFRESH_SECONDS : prev - 1));
    }, 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };

  }, [loggedInUserRole]);

  const fetchPrompts = (showLoader = false) => {
    if (showLoader) setLoading(true);
    getApiCall(`${API_PROMPT_ACTIVE_LIST}?prompt_status=0`)
      .then((resp) => {
        const updated = resp.map((item, i) => ({ ...item, key: i + 1, status: 'Pending' }));
        setPrompts(updated);
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to load pending prompts'))
      .finally(() => setLoading(false));
  };

  const handleLanguageStatus = (promptId, language, status) => {
    postApiCall(`/v2/prompt/${promptId}/language-status`, { language, status })
      .then((res) => {
        const isApproved = status === PROMPT_STATUS.APPROVED;
        const updatedEntry = res?.entry || null;
        toast.success(
          isApproved
            ? `Voice file (${language.toUpperCase()}) has been approved successfully.`
            : `Voice file (${language.toUpperCase()}) has been rejected.`,
        );
        const mergeEntry = (e) => {
          if (e.language !== language) return e;
          return updatedEntry ? { ...updatedEntry } : { ...e, status };
        };
        setFilesModal((prev) => ({
          ...prev,
          entries: prev.entries.map(mergeEntry),
        }));
        setPrompts((prev) =>
          prev
            .map((p) => {
              if (p.id !== promptId) return p;
              const updatedUrl = Array.isArray(p.prompt_url) ? p.prompt_url.map(mergeEntry) : p.prompt_url;
              return { ...p, prompt_url: updatedUrl };
            })
            .filter((p) => {
              if (p.id !== promptId) return true;
              const entries = Array.isArray(p.prompt_url) ? p.prompt_url : [];
              const hasApproved = entries.some((e) => e.status === PROMPT_STATUS.APPROVED);
              const stillPending = entries.some((e) => e.status === PROMPT_STATUS.PENDING);
              return !hasApproved && stillPending;
            }),
        );
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to update prompt status'));
  };

  const openFilesModal = (rowData) => {
    const entries = Array.isArray(rowData.prompt_url)
      ? rowData.prompt_url
      : rowData.prompt_url
        ? [{ language: 'hi', url: rowData.prompt_url, duration: rowData.prompt_duration || 0, status: rowData.prompt_status || 0 }]
        : [];
    setFilesModal({ visible: true, entries, promptName: rowData.prompt_name, promptId: rowData.id });
    setPlayingUrl('');
  };

  const closeFilesModal = () => {
    setFilesModal({ visible: false, entries: [], promptName: '', promptId: null });
    setPlayingUrl('');
  };

  const downloadFile = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'audio';
    a.target = '_blank';
    a.click();
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'prompt_name', header: 'Name' },
      { accessorKey: 'prompt_duration', header: 'Total Duration' },
      { id: 'creator', header: 'Created By', cell: ({ row }) => row.original.creator?.name || '—' },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => { e.stopPropagation(); openFilesModal(row.original); }}
            title="View Files"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Eye className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <>
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground">Pending Prompts</span>
            {prompts.length > 0 && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                {prompts.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => { fetchPrompts(false); setRefreshCountdownSec(AUTO_REFRESH_SECONDS); }}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Refresh Pending Prompts"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {refreshCountdownSec}s
          </button>
        </div>

        <DataTable
          tableId="dashboard.pending-prompts"
          columns={columns}
          data={prompts}
          isLoading={loading}
          searchPlaceholder="Search prompts…"
          emptyTitle="No pending prompts"
          emptyDescription="All prompts have been reviewed."
          pageSize={10}
          exportFilename="pending-prompts.csv"
        />
      </>

      <Dialog open={filesModal.visible} onOpenChange={(open) => !open && closeFilesModal()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Voice Files — {filesModal.promptName}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2.5 font-bold">Language</th>
                  <th className="px-3 py-2.5 font-bold">Duration</th>
                  <th className="px-3 py-2.5 font-bold">Status</th>
                  <th className="px-3 py-2.5 text-center font-bold">Listen / Download</th>
                  <th className="px-3 py-2.5 text-center font-bold">Approve / Reject</th>
                </tr>
              </thead>
              <tbody>
                {filesModal.entries.map((entry, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2.5">
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold uppercase text-blue-700">
                        {entry.language || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">{entry.duration ? `${entry.duration}s` : '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
                        (entry.status == 1 ? 'bg-emerald-100 text-emerald-700'
                          : entry.status == 2 ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700')
                      }>
                        {entry.status == 1 ? 'Approved' : entry.status == 2 ? 'Rejected' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <button onClick={() => setPlayingUrl(playingUrl === entry.url ? '' : entry.url)} title={playingUrl === entry.url ? 'Stop' : 'Listen'} className="text-primary">
                          {playingUrl === entry.url ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                        <button onClick={() => downloadFile(entry.url, `${filesModal.promptName}_${entry.language}`)} title="Download" className="text-emerald-600">
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          title="Approve"
                          disabled={entry.status == PROMPT_STATUS.APPROVED}
                          onClick={() => handleLanguageStatus(filesModal.promptId, entry.language, PROMPT_STATUS.APPROVED)}
                          className={'text-emerald-600 ' + (entry.status == PROMPT_STATUS.APPROVED ? 'cursor-not-allowed opacity-40' : '')}
                        >
                          <CheckCircle2 className="h-5 w-5" />
                        </button>
                        <button
                          title="Reject"
                          disabled={entry.status == PROMPT_STATUS.REJECT}
                          onClick={() => handleLanguageStatus(filesModal.promptId, entry.language, PROMPT_STATUS.REJECT)}
                          className={'text-red-500 ' + (entry.status == PROMPT_STATUS.REJECT ? 'cursor-not-allowed opacity-40' : '')}
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {playingUrl && (
            <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Now Playing</p>
              <audio key={playingUrl} controls autoPlay className="w-full">
                <source src={playingUrl} type="audio/mpeg" />
              </audio>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
